import { useMemo, useState, type ReactNode } from 'react';
import { Alert, Empty, Input, Segmented, Space, Tree, Typography, type TreeDataNode } from 'antd';
import type { ActivityEvent, TraceTelemetry } from '@/api/activity';
import { StatusPill } from '@/components/StatusPill';
import { activityTree, executionTree, recordedWindow, type RequestNode } from './requestTree';

const { Text } = Typography;

export function RequestTreeView({ events, telemetry, telemetryLoading, telemetryError, configured, singleRequest = true, renderEvent }: {
  events: ActivityEvent[];
  telemetry: TraceTelemetry | null;
  telemetryLoading: boolean;
  telemetryError: string | null;
  configured: boolean | null;
  singleRequest?: boolean;
  renderEvent: (event: ActivityEvent) => ReactNode;
}) {
  const [source, setSource] = useState<'activity' | 'execution'>('activity');
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const roots = useMemo(() => source === 'activity' ? activityTree(events) : executionTree(telemetry), [source, events, telemetry]);
  const all: RequestNode[] = [];
  const flatten = (nodes: RequestNode[]) => nodes.forEach((n) => { all.push(n); flatten(n.children); });
  flatten(roots);
  const picked = all.find((n) => n.key === selected) ?? all.find((n) => n.event || n.span);
  const needle = search.trim().toLowerCase();
  const matching = (n: RequestNode): boolean => n.label.toLowerCase().includes(needle) || n.children.some(matching);
  const data = (nodes: RequestNode[]): TreeDataNode[] => nodes.filter((n) => !needle || matching(n)).map((n) => ({
    key: n.key,
    title: <span className="request-tree-title"><span>{n.label}</span>
      {(n.event || n.span) && <StatusPill tone={(n.event?.errored || n.span?.success === false) ? 'red' : 'slate'}>
        {(n.event?.errored || n.span?.success === false) ? 'Error' : 'Recorded'}
      </StatusPill>}
    </span>,
    children: data(n.children),
  }));
  const window = recordedWindow(roots);
  return <section aria-label="Request execution explorer" style={{ padding: 18 }}>
    <style>{`
      .request-tree-layout { display: grid; grid-template-columns: minmax(240px, .85fr) minmax(0, 1.15fr); gap: 16px; margin-top: 14px; }
      .request-tree-title { display: inline-flex; gap: 6px; align-items: baseline; white-space: normal; overflow-wrap: anywhere; }
      .request-tree-detail { min-width: 0; overflow-wrap: anywhere; }
      .request-tree-meta { display: grid; grid-template-columns: auto minmax(0,1fr); gap: 8px 14px; margin-top: 16px; }
      .request-tree-meta dd { margin: 0; }
      @media (max-width: 767px) { .request-tree-layout { grid-template-columns: minmax(0,1fr); } }
    `}</style>
    <Space direction="vertical" style={{ width: '100%' }} size={10}>
      <Segmented aria-label="Evidence source" value={source} onChange={(v) => { setSource(v as typeof source); setSelected(null); }}
        options={[{ label: 'Recorded activity', value: 'activity' }, { label: 'Execution spans', value: 'execution', disabled: !singleRequest }]} />
      {!singleRequest && <Text type="secondary">Open an individual request from Activity to inspect its execution spans.</Text>}
      <Text type="secondary">Branches use recorded parent links. Steps with missing or ambiguous parents stay separate.
        SQL activity and execution spans are separate views of the same request; their counts and durations must not be added together.</Text>
      {source === 'execution' && <>
        <Text type="secondary">Recorded window: {window == null ? 'unavailable' : `${(window / 1000).toFixed(2)} s`}. Overlapping spans are not summed.
          Sampling, retention and missing practice attribution can leave gaps.</Text>
        {configured === false && <Alert type="info" showIcon title="Execution telemetry is not configured" description="Recorded activity remains available. Configure the Application Insights query connection to inspect native parent-child spans." />}
        {telemetryLoading && <Alert type="info" showIcon title="Loading execution spans…" />}
        {telemetryError && <Alert type="warning" showIcon title="Execution spans could not be loaded" description={telemetryError} />}
        {telemetry?.truncated && <Alert type="warning" showIcon title="Showing the first 1,000 telemetry records" description="Additional records were omitted. Some parents may be outside this result." />}
      </>}
    </Space>
    {roots.length === 0 ? <Empty description={source === 'execution' ? 'No execution spans recorded for this request in the queried window.' : 'No activity recorded.'} /> :
      <div className="request-tree-layout">
        <div style={{ minWidth: 0 }}>
          <Input.Search aria-label="Find a recorded step" placeholder="Find a model, tool or specialist…" value={search} onChange={(e) => setSearch(e.target.value)} allowClear />
          <Tree key={`${source}:${needle}`} aria-label="Recorded request tree" blockNode showLine defaultExpandAll height={420}
            treeData={data(roots)} selectedKeys={picked ? [picked.key] : []} style={{ marginTop: 10, background: 'transparent' }}
            onSelect={(keys) => setSelected(keys.length ? String(keys[0]) : null)} />
        </div>
        <div className="request-tree-detail" aria-label="Selected step evidence">
          {picked && <>
            <Text strong>{picked.label}</Text><br /><Text type="secondary">{picked.relation}</Text>
            <div style={{ marginTop: 12 }}>
              {picked.event ? renderEvent(picked.event) : picked.span ? <>
                <StatusPill tone={picked.span.success ? 'green' : 'red'}>{picked.span.success ? 'Succeeded' : 'Failed'}</StatusPill>
                <dl className="request-tree-meta">
                  <dt>Started</dt><dd>{picked.span.timestamp}</dd>
                  <dt>Duration</dt><dd>{picked.span.durationMs} ms</dd>
                  <dt>Kind</dt><dd>{picked.span.type || 'Not recorded'}</dd>
                  <dt>Target</dt><dd>{picked.span.target || 'Not recorded'}</dd>
                  <dt>Result</dt><dd>{picked.span.resultCode || 'Not recorded'}</dd>
                  <dt>Service</dt><dd>{picked.span.cloudRoleName || 'Not recorded'}</dd>
                  <dt>Span</dt><dd>{picked.span.spanId || 'Not recorded'}</dd>
                  <dt>Parent</dt><dd>{picked.span.parentSpanId || 'Not recorded'}</dd>
                </dl>
              </> : <Text type="secondary">Select a recorded step to inspect its evidence.</Text>}
            </div>
          </>}
        </div>
      </div>}
  </section>;
}
